import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ServiceService } from '../service.service';
import { ScreenCaptureService } from './screen-capture.service';

/**
 * Regex that detects "the user wants the AI to look at the current screen".
 * Matches phrases like:
 *   - "analyze my screen", "analyse this page", "read the screen"
 *   - "look at this page", "look at the screen"
 *   - "what am I looking at", "what's on this page"
 *   - "describe / explain / review / check this screen|page"
 *   - "see my screen", "see what i'm seeing"
 *   - "what do you see"
 * Kept intentionally broad — user said "similar prompts should do this".
 */
const SCREEN_INTENT_REGEX = new RegExp(
  // verb phrases targeting screen/page
  '\\b(analy[sz]e|read|check|describe|explain|review|look\\s+at|see)\\b' +
  '[^.?!]{0,40}\\b(my\\s+)?(screen|page|view|current\\s+page|this\\s+page|this\\s+screen)\\b'
  // OR direct "what am i looking at" / "what's on this page" / "what do you see"
  + '|\\bwhat\\s+(am\\s+i|are\\s+you)\\s+(looking\\s+at|seeing)\\b'
  + '|\\bwhat(\'|`)?s\\s+on\\s+(this|the|my)\\s+(page|screen)\\b'
  + '|\\bwhat\\s+do\\s+you\\s+see\\b',
  'i'
);

/**
 * Shared chat state — both the floating widget (`app-ai-assistant`)
 * and the full-page route (`app-ai-chat-page`) subscribe to this single
 * source of truth so they always show the same history, the same
 * active conversation, and the same in-flight stream.
 */

export interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'horizontalBar' | 'donut' | 'stackedBar' | 'area' | 'scatter' | 'radar';
  title: string;
  xAxis?: string[];
  series: Array<{ name: string; data: number[] }>;
  unit?: string;
}

export interface DocFile {
  name: string;
  format: 'pdf' | 'excel' | 'word' | 'ppt';
  url: string;
  sizeKb: number;
}

export interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  time: string;
  suggestions?: string[];
  chartData?: ChartData | null;
  chartOptions?: any;
  mapData?: any | null;   // getVisitMap heat-map payload {rep,period,totals,center,points[]}
  /** Generated downloadable report files (createDocument) → download cards. */
  files?: DocFile[];
  finalized?: boolean;
  /** True while the typewriter is still revealing this message's text
   *  (drives the blinking caret; may stay true briefly after finalized). */
  typing?: boolean;
  status?: string;
  steps?: string[];
  stepsOpen?: boolean;
  processingStart?: number;
  processingDuration?: number;
  /** True when the user message had a screen capture attached. */
  screenAttached?: boolean;
  /** v2.9 (C.2): optimistic UI delivery state on user messages.
   *  'pending'   — POST in flight, no response yet (subtle "..." indicator)
   *  'delivered' — server accepted (first SSE chunk arrived) — checkmark
   *  'failed'    — network/server error; user can retry */
  delivery?: 'pending' | 'delivered' | 'failed';
  /** Failure reason shown next to the retry button when delivery='failed'. */
  deliveryError?: string;
}

export interface ChatSession {
  session_id: string;
  title: string;
  updatedAt: string;
  createdAt: string;
}

export interface SessionGroup {
  label: string;
  sessions: ChatSession[];
}

@Injectable({ providedIn: 'root' })
export class AiChatStateService {

  // ── Observables ────────────────────────────────────────────────────
  private readonly _sessions$ = new BehaviorSubject<ChatSession[]>([]);
  readonly sessions$: Observable<ChatSession[]> = this._sessions$.asObservable();

  private readonly _sessionGroups$ = new BehaviorSubject<SessionGroup[]>([]);
  readonly sessionGroups$: Observable<SessionGroup[]> = this._sessionGroups$.asObservable();

  private readonly _messages$ = new BehaviorSubject<ChatMessage[]>([]);
  readonly messages$: Observable<ChatMessage[]> = this._messages$.asObservable();

  private readonly _currentSessionId$ = new BehaviorSubject<string | null>(null);
  readonly currentSessionId$: Observable<string | null> = this._currentSessionId$.asObservable();

  private readonly _currentTitle$ = new BehaviorSubject<string>('New chat');
  readonly currentTitle$: Observable<string> = this._currentTitle$.asObservable();

  private readonly _isLoading$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$: Observable<boolean> = this._isLoading$.asObservable();

  private readonly _isStreaming$ = new BehaviorSubject<boolean>(false);
  readonly isStreaming$: Observable<boolean> = this._isStreaming$.asObservable();

  private readonly _suggestions$ = new BehaviorSubject<string[]>([]);
  readonly suggestions$: Observable<string[]> = this._suggestions$.asObservable();

    private readonly _elapsedSecs$ = new BehaviorSubject<number>(0);
  readonly elapsedSecs$: Observable<number> = this._elapsedSecs$.asObservable();

    private readonly _quotaPercent$ = new BehaviorSubject<number>(0);
  readonly quotaPercent$: Observable<number> = this._quotaPercent$.asObservable();

  private readonly _quotaUnlimited$ = new BehaviorSubject<boolean>(false);
  readonly quotaUnlimited$: Observable<boolean> = this._quotaUnlimited$.asObservable();

  loadQuota(userName: string, userType: string): void {
    this.service.getAiQuota().subscribe({
      next: (res: any) => {
        const unlimited = res?.unlimited === true || res?.limit == null;
        this._quotaUnlimited$.next(unlimited);
        if (unlimited) { this._quotaPercent$.next(0); return; }
        const used = res?.used ?? 0;
        const limit = res?.limit ?? 0;
        const pct = limit > 0 ? Math.round((used / limit) * 100) : 0;
        this._quotaPercent$.next(Math.max(0, Math.min(100, pct)));
      },
      error: () => { /* keep last known value */ }
    });
  }
  // Live "thinking…" line — each brand phrase types itself in, then cycles.
  // Shared by widget + full-page so both stay identical.
  private readonly _thinkingDisplay$ = new BehaviorSubject<string>('Thinking…');
  readonly thinkingDisplay$: Observable<string> = this._thinkingDisplay$.asObservable();

  // ── Internal ──────────────────────────────────────────────────────
  private abortController: AbortController | null = null;
  private cdTimer: any = null;
  private processingStart = 0;
  private lastUserName = '';
  // Typewriter reveal of streamed answers + the thinking line.
  private typeTimer: any = null;
  private thinkTimer: any = null;
  private typeTarget = '';          // full accumulated text for the active reply
  private typeMsgIndex = -1;        // which message the typewriter is revealing
  private streamEnded = false;      // SSE fully received (lets the typer drain then stop)
  private readonly TYPE_TICK_MS = 24;    // answer reveal cadence
  private readonly THINK_TYPE_MS = 38;   // thinking-phrase per-character cadence

  constructor(
    private service: ServiceService,
    private ngZone: NgZone,
    private screenCapture: ScreenCaptureService
  ) {}

  /** Exposed so other parts of the UI can ask "would this prompt trigger a screen capture?" */
  wantsScreenCapture(text: string): boolean {
    return SCREEN_INTENT_REGEX.test(text || '');
  }

  // ── Synchronous accessors ─────────────────────────────────────────
  get sessions(): ChatSession[]       { return this._sessions$.value; }
  get messages(): ChatMessage[]       { return this._messages$.value; }
  get currentSessionId(): string|null { return this._currentSessionId$.value; }
  get currentTitle(): string          { return this._currentTitle$.value; }
  get isLoading(): boolean            { return this._isLoading$.value; }
  get isStreaming(): boolean          { return this._isStreaming$.value; }
  get suggestions(): string[]         { return this._suggestions$.value; }
  get elapsedSecs(): number           { return this._elapsedSecs$.value; }

  // ── Public API ────────────────────────────────────────────────────

  // ── Conversation continuity across reloads ───────────────────────
  // People keep asking follow-ups over hours/days. Remember each user's last
  // open conversation so a page reload / app reopen CONTINUES it instead of
  // starting blank. Cleared on "New chat"; ignored if it belongs to another user.
  private rememberLastSession(userName: string | undefined, sessionId: string): void {
    try { if (userName) localStorage.setItem('shajiLastSession', JSON.stringify({ u: userName, id: sessionId })); } catch {}
  }
  private forgetLastSession(): void {
    try { localStorage.removeItem('shajiLastSession'); } catch {}
  }

  /** Fetch the user's session list and group it by recency. */
  loadSessions(userName: string): void {
    // Safety net: if the logged-in identity changed in this same browser tab
    // (e.g. logout → login as another user without a full reload), drop the
    // previous user's in-memory view so their chats never linger on screen.
    // (Server-side isolation by user_id is the real guard; this is cosmetic.)
    if (this.lastUserName && this.lastUserName !== userName) {
      this._messages$.next([]);
      this._sessions$.next([]);
      this._sessionGroups$.next([]);
      this._currentSessionId$.next(null);
      this._currentTitle$.next('New chat');
      this._suggestions$.next([]);
      this.forgetLastSession();
    }
    this.lastUserName = userName;
    this.service.getAiSessions().subscribe({
      next: (res: any) => {
        if (res?.success === 'true') {
          const sessions: ChatSession[] = res.sessions || [];
          this._sessions$.next(sessions);
          this._sessionGroups$.next(this.groupSessions(sessions));
          // NOTE: no auto-resume. Owner's rule (2026-06-14): opening the
          // assistant always starts on a fresh "New chat" — past chats stay in
          // the sidebar for the user to open deliberately. (Removed the old
          // shajiLastSession auto-reopen.)
        }
      },
      error: () => { /* keep last good list */ }
    });
  }

  /** Reset to a blank "New chat" state. Preserves the loaded session list. */
  newChat(employeeName: string): void {
    this.stopStreaming();
    this.forgetLastSession();
    this._currentSessionId$.next(null);
    this._currentTitle$.next('New chat');
    this._suggestions$.next([]);
    this._messages$.next([this.welcomeMessage(employeeName)]);
  }

  /** Load a stored session into the active thread. */
  openSession(session: ChatSession, userName?: string): void {
    this.stopStreaming();
    this.rememberLastSession(userName || this.lastUserName, session.session_id);
    this._currentSessionId$.next(session.session_id);
    this._currentTitle$.next(session.title);
    this._suggestions$.next([]);
    this._messages$.next([]);
    this._isLoading$.next(true);

    this.service.getAiSession(session.session_id).subscribe({
      next: (res: any) => {
        this._isLoading$.next(false);
        if (res?.success === 'true' && res.session) {
          const loaded: ChatMessage[] = res.session.messages.map((m: any) => {
            const chartData = m.chart_data || null;
            return {
              role: m.role === 'assistant' ? 'ai' : 'user',
              text: m.content,
              time: new Date(m.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
              chartData,
              chartOptions: chartData ? this.buildChartOptions(chartData) : null,
              mapData: m.map_data || null,
              files: Array.isArray(m.files) ? m.files : undefined,
              finalized: true
            } as ChatMessage;
          });
          this._messages$.next(loaded);
          // Let charts size up after the layout commits
          setTimeout(() => window.dispatchEvent(new Event('resize')), 150);
        }
      },
      error: () => this._isLoading$.next(false)
    });
  }

  /** Delete a stored session (and locally clear it if it was active). */
  deleteSession(sessionId: string, employeeName: string): void {
    this.service.deleteAiSession(sessionId).subscribe({
      next: () => {
        const remaining = this.sessions.filter(s => s.session_id !== sessionId);
        this._sessions$.next(remaining);
        this._sessionGroups$.next(this.groupSessions(remaining));
        if (this.currentSessionId === sessionId) {
          this.newChat(employeeName);
        }
        // Re-load from server to stay in sync
        this.loadSessions(this.lastUserName || employeeName);
      },
      error: () => { /* swallow — UI will show stale list */ }
    });
  }

  /** Rename a stored session. Optimistic local update; reverts (re-fetches) on error. */
  renameSession(sessionId: string, newTitle: string, employeeName: string): void {
    const clean = (newTitle || '').trim().replace(/\s+/g, ' ').slice(0, 80);
    if (!clean) return;
    const updated = this.sessions.map(s => s.session_id === sessionId ? { ...s, title: clean } : s);
    this._sessions$.next(updated);
    this._sessionGroups$.next(this.groupSessions(updated));
    this.service.renameAiSession(sessionId, clean).subscribe({
      next: () => { /* persisted */ },
      error: () => { this.loadSessions(this.lastUserName || employeeName); }
    });
  }

  /** Cancel any in-flight stream. */
  stopStreaming(): void {
    this.abortController?.abort();
    this.abortController = null;
    this._isStreaming$.next(false);
    this._isLoading$.next(false);
    if (this.cdTimer) { clearInterval(this.cdTimer); this.cdTimer = null; }
    // Halt the typewriter + thinking ticker so they can't write into a thread
    // that's being replaced (new chat / open session).
    this.streamEnded = true;
    this.stopTypewriter();
    this.stopThinkingTyper();
    this._elapsedSecs$.next(0);
  }

  /** Send a message and stream the response via SSE. */
  /** Upload a PDF/Word/Excel for server-side text extraction + the company-relevance
   *  gate. Shared by the widget and full-page chat. Returns the backend's verdict:
   *  { ok:true, docId, filename, truncated } on accept, or { ok:false, message } on reject. */
  async uploadDocument(file: File, userType: string, userName: string): Promise<any> {
    try {
      const res = await this.service.extractDocument(file, userName, userType);
      return res || { ok: false, message: "I couldn't read that file." };
    } catch {
      return { ok: false, message: 'Upload failed — please check your connection and try again.' };
    }
  }

  async sendMessage(text: string, userType: string, userName: string, attachedPhotoBase64?: string | null, attachedDocId?: string | null): Promise<void> {
    const trimmed = (text || '').trim();
    if (!trimmed || this.isLoading || this.isStreaming) return;

    this.shuffleThinking();   // fresh random phrase order for this reply
    this.lastUserName = userName;
    const msgs = [...this.messages];

    // If the visible thread is still the welcome screen, drop it now —
    // a user's first real message replaces the greeting.
    if (msgs.length === 1 && msgs[0].role === 'ai' && msgs[0].finalized && msgs[0].text.startsWith('Hello')) {
      msgs.length = 0;
    }

    // Screen-reading intent detection: if the user asked the AI to look at
    // the screen, capture a JPEG + structured DOM context BEFORE pushing the
    // user bubble so the bubble can show a 📎 indicator.
    const wantsScreen = this.wantsScreenCapture(trimmed);

    // v2.9 (C.2): stamp 'pending' so the bubble can show a "delivering..." dot.
    // Flipped to 'delivered' when the first SSE chunk arrives below.
    msgs.push({ role: 'user', text: trimmed, time: this.nowTime(), screenAttached: wantsScreen, delivery: 'pending' });
    const userMsgIndex = msgs.length - 1;

    this.processingStart = Date.now();
    const aiMsgIndex = msgs.length;
    // Reset the typewriter for this new reply.
    this.stopTypewriter();
    this.typeTarget = '';
    this.typeMsgIndex = aiMsgIndex;
    this.streamEnded = false;
    msgs.push({
      role: 'ai', text: '', time: this.nowTime(), chartData: null, mapData: null,
      finalized: false,
      status: wantsScreen ? 'Capturing your screen...' : 'Thinking...',
      steps: [wantsScreen ? 'Capturing your screen...' : 'Thinking...'],
      stepsOpen: true, processingStart: this.processingStart
    });

    this._messages$.next(msgs);
    this._suggestions$.next([]);
    this._isLoading$.next(true);
    this._isStreaming$.next(false);

    // Live elapsed-time ticker
    this._elapsedSecs$.next(0);
    clearInterval(this.cdTimer);
    this.cdTimer = setInterval(() => {
      this.ngZone.run(() => this._elapsedSecs$.next(Math.floor((Date.now() - this.processingStart) / 1000)));
    }, 1000);
    // Type the cycling "thinking…" brand phrases in, character by character.
    this.startThinkingTyper();

    this.abortController = new AbortController();

    // Image input has two sources:
    //   1. wantsScreen + html2canvas → screenshot of ERP screen + screen_context (route, pageTitle...)
    //   2. attachedPhotoBase64 → user uploaded an invoice/receipt photo for OCR (no context)
    // Backend's system prompt differentiates: image WITH context = describe screen, image WITHOUT context = OCR.
    let screenshot: string | undefined;
    let screen_context: any | undefined;
    if (attachedPhotoBase64) {
      screenshot = attachedPhotoBase64;
      const prevSteps = this.messages[aiMsgIndex]?.steps || [];
      this.patchAi(aiMsgIndex, {
        status: 'Reading the photo...',
        steps: [...prevSteps, 'Reading the photo...']
      });
    } else if (wantsScreen) {
      try {
        const cap = await this.screenCapture.capture();
        if (cap.imageBase64) screenshot = cap.imageBase64;
        screen_context = cap.context;
        const prevSteps = this.messages[aiMsgIndex]?.steps || [];
        this.patchAi(aiMsgIndex, {
          status: 'Analyzing your screen...',
          steps: [...prevSteps, 'Analyzing your screen...']
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[AiChatState] screen capture failed, continuing text-only.', e);
      }
    }

    try {
      const response = await this.service.streamAiMessage({
        message: trimmed,
        user_type: userType,
        user_name: userName,
        session_id: this.currentSessionId || undefined,
        screenshot,
        screen_context,
        doc_id: attachedDocId || undefined
      }, this.abortController.signal);

      if (!response.ok || !response.body) {
        this.ngZone.run(() => {
          this.patchAi(aiMsgIndex, { text: 'Stream unavailable. Please refresh.', status: '', finalized: true, typing: false });
          // v2.9 (C.2): mark the user message as failed so a retry button can render.
          this.patchUser(userMsgIndex, { delivery: 'failed', deliveryError: `HTTP ${response.status || 'unknown'}` });
          this._isLoading$.next(false);
        });
        return;
      }

      this.ngZone.run(() => {
        this._isLoading$.next(false);
        this._isStreaming$.next(true);
        // v2.9 (C.2): server accepted + first bytes are flowing.
        this.patchUser(userMsgIndex, { delivery: 'delivered' });
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = 'text';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        const parsedEvents: Array<{ event: string; data: string }> = [];
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            parsedEvents.push({ event: currentEvent, data: line.slice(6) });
            currentEvent = 'text';
          }
        }

        for (const ev of parsedEvents) {
          this.ngZone.run(() => this.handleSseData(ev.event, ev.data, aiMsgIndex));
        }
      }

    } catch (err: any) {
      // Stop the typewriter immediately so it can't overwrite the message we're
      // about to set with leftover buffered text.
      this.stopTypewriter();
      this.streamEnded = true;
      const isAbort = err && (err.name === 'AbortError' || (err instanceof DOMException && err.name === 'AbortError'));
      this.ngZone.run(() => {
        const currentText = this.messages[aiMsgIndex]?.text || '';
        if (isAbort) {
          this.patchAi(aiMsgIndex, { text: currentText || 'Response stopped.', status: '', finalized: true, typing: false });
          // Aborts are user-initiated — count as delivered (server got our request).
          if (this.messages[userMsgIndex]?.delivery === 'pending')
            this.patchUser(userMsgIndex, { delivery: 'delivered' });
        } else {
          this.patchAi(aiMsgIndex, { text: 'Connection error. Please make sure the backend server is running.', status: '', finalized: true, typing: false });
          // v2.9 (C.2): mark the user bubble failed so a retry button can render.
          this.patchUser(userMsgIndex, { delivery: 'failed', deliveryError: err?.message || 'Network error' });
          this._suggestions$.next([]);
        }
      });
        } finally {
      // Stream fully received: let the typewriter drain the rest, then stop.
      this.streamEnded = true;
      this.stopThinkingTyper();
      clearInterval(this.cdTimer);
      this.cdTimer = null;
      this._elapsedSecs$.next(0);
      const duration = Date.now() - this.processingStart;
      this.abortController = null;
            this.ngZone.run(() => {
        this.patchAi(aiMsgIndex, { finalized: true, status: '', stepsOpen: false, processingDuration: duration });
        // A stray mid-stream 'error' SSE event may have marked the user bubble
        // 'failed' even though real content still streamed in afterward. If the
        // AI ended up with actual text, the send clearly succeeded — clear it.
        if (this.typeTarget && this.typeTarget.trim() && this.messages[userMsgIndex]?.delivery === 'failed') {
          this.patchUser(userMsgIndex, { delivery: 'delivered', deliveryError: undefined });
        }
        this._isLoading$.next(false);
        this._isStreaming$.next(false);
        this.loadSessions(userName);
        this.loadQuota(userName, userType);
      });
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  private handleSseData(event: string, data: string, msgIndex: number): void {
    const msg = this.messages[msgIndex];
    if (!msg) return;

    if (event === 'status') {
      let statusText = data;
      try { statusText = JSON.parse(data); } catch {}
      const existingSteps = msg.steps || [];
      const lastStep = existingSteps[existingSteps.length - 1];
      const updatedSteps = lastStep === statusText ? existingSteps : [...existingSteps, statusText];
      const keepStatusVisible = !msg.text;
      this.patchAi(msgIndex, {
        status: keepStatusVisible ? statusText : msg.status,
        steps: updatedSteps
      });
    } else if (event === 'text') {
      let text = data;
      try { text = JSON.parse(data); } catch {}
      // Don't dump the chunk straight in — buffer it and let the typewriter
      // reveal it at an even pace.
      this.typeTarget += text;
      this.ensureTypewriter(msgIndex);
    } else if (event === 'chart') {
      try {
        const chartData = JSON.parse(data);
        const chartOptions = this.buildChartOptions(chartData);
        this.patchAi(msgIndex, { chartData, chartOptions });
      } catch {}
    } else if (event === 'map') {
      // Interactive visit heat-map (getVisitMap) → the VisitMapDirective renders it.
      try { this.patchAi(msgIndex, { mapData: JSON.parse(data) }); } catch {}
    } else if (event === 'file') {
      // Generated document → append a download card to this message.
      try {
        const file = JSON.parse(data);
        const existing = this.messages[msgIndex]?.files || [];
        this.patchAi(msgIndex, { files: [...existing, file] });
      } catch {}
    } else if (event === 'done') {
      try {
        const info = JSON.parse(data);
        if (info.session_id) {
          this._currentSessionId$.next(info.session_id);
          this.rememberLastSession(this.lastUserName, info.session_id);   // continuity across reloads
        }
        if (info.title) this._currentTitle$.next(info.title);
        const latest = this.messages[msgIndex];
        // Use the full buffered reply (the displayed text may still be typing).
        this._suggestions$.next(this.extractSuggestions(this.typeTarget || latest?.text || ''));
      } catch {}
    } else if (event === 'error') {
      // D.3: backend now sends { message, retryable } (older builds sent a bare
      // string). Handle both. When retryable, flag the preceding user bubble as
      // failed so the Retry affordance shows.
      let payload: any = data;
      try { payload = JSON.parse(data); } catch {}
      const errorText: string =
        (payload && typeof payload === 'object' ? payload.message : payload) ||
        'An error occurred. Please try again.';
      const retryable = !(payload && typeof payload === 'object' && payload.retryable === false);
      this.patchAi(msgIndex, { text: errorText, status: '' });

      const uIdx = msgIndex - 1;
      if (retryable && this.messages[uIdx]?.role === 'user') {
        this.patchUser(uIdx, { delivery: 'failed', deliveryError: errorText });
      }
    }
  }

  private patchAi(idx: number, patch: Partial<ChatMessage>): void {
    const msgs = [...this.messages];
    if (!msgs[idx]) return;
    msgs[idx] = { ...msgs[idx], ...patch };
    this._messages$.next(msgs);
  }

  // ── Typewriter reveal (smooth, even character-by-character) ────────
  // SSE delivers text in uneven bursts; we buffer the full text in
  // `typeTarget` and reveal `msg.text` a few characters per tick so every
  // answer "types" in at a calm, steady pace on both surfaces.
  private ensureTypewriter(msgIndex: number): void {
    this.typeMsgIndex = msgIndex;
    // Once the answer starts, the cycling "thinking…" line gives way to the
    // typing answer — stop its ticker.
    this.stopThinkingTyper();
    if (this.typeTimer) return;
    this.typeTimer = setInterval(() => this.ngZone.run(() => this.typeTick()), this.TYPE_TICK_MS);
  }

  private typeTick(): void {
    const idx = this.typeMsgIndex;
    const m = this.messages[idx];
    if (!m || m.role !== 'ai') { this.stopTypewriter(); return; }
    const shown = m.text.length;
    const target = this.typeTarget.length;
    if (shown >= target) {
      if (this.streamEnded) {
        if (m.typing) this.patchAi(idx, { typing: false });
        this.stopTypewriter();
      }
      return;
    }
    const remaining = target - shown;
    // Gentle catch-up: a little faster the further behind we are, but always
    // visibly typing. Drains a touch quicker once the full reply has arrived.
    const cap = this.streamEnded ? 9 : 4;
    const step = Math.max(1, Math.min(cap, Math.ceil(remaining / 22)));
    this.patchAi(idx, { text: this.typeTarget.slice(0, shown + step), typing: true, status: '' });
  }

  private stopTypewriter(): void {
    if (this.typeTimer) { clearInterval(this.typeTimer); this.typeTimer = null; }
  }

  // ── "Thinking…" line — types each brand phrase in, then cycles ─────
  private startThinkingTyper(): void {
    if (!this._thinkOrder.length) this.shuffleThinking();
    this._thinkingDisplay$.next('');
    clearInterval(this.thinkTimer);
    this.thinkTimer = setInterval(() => this.ngZone.run(() => {
      const ms = Date.now() - this.processingStart;
      const windowMs = this._thinkDurationS * 1000;
      const w = Math.floor(ms / windowMs);
      const phrase = this.thinkingPhrases[this._thinkOrder[w % this._thinkOrder.length]] || '';
      const into = ms - w * windowMs;
      const reveal = Math.min(phrase.length, Math.max(1, Math.floor(into / this.THINK_TYPE_MS)));
      this._thinkingDisplay$.next(phrase.slice(0, reveal));
    }), 40);
  }

  private stopThinkingTyper(): void {
    if (this.thinkTimer) { clearInterval(this.thinkTimer); this.thinkTimer = null; }
  }

  /** v2.9 (C.2): patch a user-message bubble. Same shape as patchAi but distinct
   *  to make grep + intent clear when reading. */
  private patchUser(idx: number, patch: Partial<ChatMessage>): void {
    const msgs = [...this.messages];
    if (!msgs[idx] || msgs[idx].role !== 'user') return;
    msgs[idx] = { ...msgs[idx], ...patch };
    this._messages$.next(msgs);
  }

  /** v2.9 (C.2): re-send a user message whose delivery='failed'. Called from
   *  the bubble's retry button. Drops the failed user bubble + matching AI
   *  placeholder so the new send replaces them. */
  retryFailedMessage(idx: number, userType: string, userName: string): void {
    const msgs = this.messages;
    if (!msgs[idx] || msgs[idx].role !== 'user' || msgs[idx].delivery !== 'failed') return;
    const failed = msgs[idx];
    const next = msgs.slice(0, idx);  // drop the failed bubble + any trailing placeholder AI
    this._messages$.next(next);
    this.sendMessage(failed.text, userType, userName);
  }

  /** Regenerate the latest answer: drop the last AI reply (and the user turn that
   *  prompted it) and re-send that same question, so the AI has another go.
   *  Mirrors retryFailedMessage. Text-only — a photo/OCR attachment isn't re-sent. */
  regenerateLast(userType: string, userName: string): void {
    const msgs = this.messages;
    let u = -1;
    for (let k = msgs.length - 1; k >= 0; k--) {
      if (msgs[k].role === 'user') { u = k; break; }
    }
    if (u < 0) return;
    const question = msgs[u].text;
    if (!question || !question.trim()) return;
    this._messages$.next(msgs.slice(0, u));   // drop the question + the answer after it
    this.sendMessage(question, userType, userName);
  }

  /** Strip light markdown so copied/spoken text reads cleanly (no **, #, table pipes). */
  plainText(md: string): string {
    return (md || '')
      .replace(/```[\s\S]*?```/g, (m) => m.replace(/```/g, '').trim())
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^\s*#{1,6}\s+/gm, '')
      .replace(/^\s*[-*]\s+/gm, '• ')
      .replace(/^\s*\|?[-:\s|]+\|?\s*$/gm, '')   // markdown table separator rows
      .replace(/\s*\|\s*/g, '   ')                // table cell pipes → spacing
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  /** Copy a message's answer to the clipboard as clean plain text. Returns success.
   *  Falls back to execCommand for older Android WebViews without the async API. */
  async copyMessageText(text: string): Promise<boolean> {
    const plain = this.plainText(text);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(plain);
        return true;
      }
    } catch { /* fall through to legacy path */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = plain;
      ta.style.position = 'fixed'; ta.style.top = '-1000px'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  }

  toggleStepsOpen(idx: number): void {
    const msgs = [...this.messages];
    const m = msgs[idx];
    if (!m) return;
    msgs[idx] = { ...m, stepsOpen: !m.stepsOpen };
    this._messages$.next(msgs);
  }

  /** Show the welcome message in the message stream. */
  showWelcome(employeeName: string): void {
    this._messages$.next([this.welcomeMessage(employeeName)]);
  }

  private welcomeMessage(employeeName: string): ChatMessage {
    return {
      role: 'ai',
      text: `Hello ${employeeName}! I'm Shaji AI, your business intelligence assistant. How can I help you today?`,
      time: this.nowTime(),
      finalized: true
    };
  }

  /** Group sessions by date band. */
  private groupSessions(sessions: ChatSession[]): SessionGroup[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const week = new Date(today); week.setDate(week.getDate() - 7);

    const buckets: { [k: string]: ChatSession[] } = {
      Today: [], Yesterday: [], 'Last 7 days': [], Earlier: []
    };
    sessions.forEach(orig => {
      // Display-clean the title (strip stray surrounding quotes/space the
      // auto-title sometimes inherits from the user's first message).
      const s = { ...orig, title: ((orig.title || '').replace(/^["'\s]+|["'\s]+$/g, '') || 'Untitled chat') };
      const d = new Date(s.updatedAt);
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (day >= today) buckets['Today'].push(s);
      else if (day >= yesterday) buckets['Yesterday'].push(s);
      else if (day >= week) buckets['Last 7 days'].push(s);
      else buckets['Earlier'].push(s);
    });
    return Object.entries(buckets)
      .filter(([, list]) => list.length > 0)
      .map(([label, sessions]) => ({ label, sessions }));
  }

  /** Extract follow-up suggestion chips from the AI reply.
   *  Skipped entirely when the AI has emitted [action:...] markup — those become
   *  proper action buttons (v2.7) and chips would be redundant + lower quality. */
  private extractSuggestions(text: string): string[] {
    // v2.7: when AI emits action buttons, skip auto-chip extraction (avoids
    // showing truncated insight sentences alongside cleaner action buttons).
    if (/\[action:[^\]]{1,120}\]/.test(text)) return [];

    const chips: string[] = [];
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const numbered = lines.filter(l => /^\d+[\.\)]\s+/.test(l));
    if (numbered.length >= 2 && numbered.length <= 6) {
      for (const line of numbered) {
        const clean = this.cleanChip(line.replace(/^\d+[\.\)]\s+/, ''));
        if (clean.length >= 3) chips.push(clean);
      }
    }
    if (chips.length === 0) {
      const bulleted = lines.filter(l => /^[•\-\*]\s+/.test(l));
      if (bulleted.length >= 2 && bulleted.length <= 6) {
        for (const line of bulleted) {
          const clean = this.cleanChip(line.replace(/^[•\-\*]\s+/, ''));
          if (clean.length >= 3) chips.push(clean);
        }
      }
    }
    // Yes/No chips ONLY for a genuine confirm/proceed question — never for an
    // open-ended "what/which/how…" question (e.g. "What do you want to dig into?"
    // must NOT offer Yes/No — that's illogical).
    if (chips.length === 0
        && /\b(shall i|should i|do you want me to|would you like me to|want me to|may i|go ahead|proceed|confirm)\b/i.test(text)
        && text.includes('?')
        && !/\b(what|which|how|who|where|when|why)\b[^?]*\?/i.test(text)) {
      chips.push('Yes, please', 'No thanks');
    }
    return chips.slice(0, 4);
  }

  /** Strip markdown + emoji, then truncate at last word boundary under 52 chars + ellipsis. */
  private cleanChip(raw: string): string {
    const cleaned = raw
      .replace(/[\u{1F300}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/[?:]+$/, '')
      .trim();
    if (cleaned.length <= 52) return cleaned;
    // Find the last whitespace at or before position 52 to break at a word boundary.
    const head = cleaned.slice(0, 52);
    const lastSpace = head.lastIndexOf(' ');
    const cut = lastSpace > 20 ? head.slice(0, lastSpace) : head;
    return cut.replace(/[\s,;:.\-—–]+$/, '') + '…';
  }

  // ── Document download helpers (createDocument cards) ──────────────

  /** Resolve a server file path (e.g. "/reports/x.pdf") to an absolute URL.
   *  Works whether aiBaseUrl is "/" (single-origin tunnel) or a full URL (dev). */
  resolveFileUrl(url: string): string {
    if (!url) return '#';
    try {
      const base = (this.service as any).aiBaseUrl || '/';
      const baseAbs = new URL(base, window.location.origin);
      return new URL(url, baseAbs).href;
    } catch { return url; }
  }

  fileLabel(format: string): string {
    return ({ pdf: 'PDF', excel: 'Excel', word: 'Word', ppt: 'PowerPoint' } as any)[format] || 'File';
  }
  fileEmoji(format: string): string {
    return ({ pdf: '📄', excel: '📊', word: '📝', ppt: '📑' } as any)[format] || '📎';
  }

  /** Brand/paint phrases that cycle, ONE at a time, while a reply is in progress.
   *  SHARED by both the widget and the full-page chat so they always stay
   *  identical — edit this one list and both surfaces update together. */
  readonly thinkingPhrases: string[] = [
    'Thinking…',
    'Shaji Woodstar is bringing out the grain…',
    'Shaji Protective is standing guard…',
    'Shaji Skorpion is laying down a tough coat…',
    'Shaji Decorative is rolling on a fresh shade…',
    'Zeal Woodstar is lifting the shine…',
    'Zeal Protective is shielding the surface…',
    'Smirdex is sanding it silky smooth…',
    'Sticol is making it stick…',
    'Mixing a fresh batch…',
    'Matching the colour code…',
    'Spraying on the clear coat…',
    'Stirring the tin…',
    'Buffing it to a high gloss…',
    'Letting the coat cure…',
    'Masking off the edges…',
    'Loading the spray gun…',
    'Checking the finish…',
    'Pulling the figures…',
    'Reading the ledger…',
    'Lining up the data…',
    'Tallying it all up…',
    'Crunching the numbers…',
    'Almost ready…',
  ];
  /** Shuffled display order — reshuffled at the start of each reply so the
   *  sequence feels random and differs every time (always leads with "Thinking…"). */
  private _thinkOrder: number[] = [];
  private readonly _thinkDurationS = 3.5;   // each phrase stays ~3.5s on screen

  shuffleThinking(): void {
    const n = this.thinkingPhrases.length;
    const rest = Array.from({ length: n }, (_, i) => i).filter(i => i !== 0);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    this._thinkOrder = [0, ...rest];   // "Thinking…" first, then a fresh random order
  }

  /** Current phrase for a given elapsed-seconds value (one at a time, ~3.5s each). */
  thinkingPhraseFor(secs: number): string {
    if (!this._thinkOrder.length) this.shuffleThinking();
    const w = Math.floor((secs || 0) / this._thinkDurationS);
    return this.thinkingPhrases[this._thinkOrder[w % this._thinkOrder.length]];
  }

  // ── Markdown / chart helpers (stateless) ──────────────────────────

  /** Convert AI markdown text → safe HTML. */
  formatMessage(text: string, streamingMode = false): string {
    if (!text) return '';
    let result = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const codeBlocks: string[] = [];
    if (!streamingMode) {
      result = result.replace(/```(\w*)[^\S\r\n]*\n([\s\S]*?)```/g, (_, lang, code) => {
        const langLabel = lang ? `<span class="code-lang">${lang}</span>` : '';
        const idx = codeBlocks.length;
        codeBlocks.push(
          `<div class="ai-code-block">${langLabel}<pre><code>${code.trimEnd()}</code></pre></div>`
        );
        return `\x00CODE${idx}\x00`;
      });
    }
    result = result.replace(/`([^`\n]+)`/g, '<code class="ai-inline-code">$1</code>');

    {
      // Tables are parsed in BOTH modes now. During streaming we render the
      // settled rows as a real table and HIDE the in-progress trailing row, so
      // raw "| | |" pipes never flash mid-stream (they used to, because table
      // parsing was skipped while streaming). The table grows row-by-row.
      const streaming = streamingMode;
      const lastLineInProgress = streaming && !result.endsWith('\n');
      const lines = result.split('\n');
      const out: string[] = [];
      let i = 0;
      const parseRow = (row: string): string[] =>
        row.split('|').map(c => c.trim())
          .filter((c, idx2, arr) => !(idx2 === 0 && c === '') && !(idx2 === arr.length - 1 && c === ''));
      const isSepRow = (r: string) => /^[\|\-:\s]+$/.test(r) && r.includes('-');

      // Tap-to-drill: detect which column holds an ENTITY name from the header,
      // then wrap that body cell so the user can tap it for drill options.
      const colType = (h: string): string => {
        const s = String(h || '').toLowerCase();
        if (/cust|client|account|\bcompany\b|party|debtor/.test(s)) return 'customer';
        if (/product|item|sku|material/.test(s)) return 'product';
        if (/brand/.test(s)) return 'brand';
        if (/salesperson|sales person|sales rep|\brep\b|employee|staff|salesman|promoter|executive/.test(s)) return 'employee';
        return '';
      };
      const nameColOf = (headerCells: string[]): { idx: number; type: string } => {
        for (let k = 0; k < headerCells.length; k++) {
          const t = colType(headerCells[k]);
          if (t) return { idx: k, type: t };
        }
        return { idx: -1, type: '' };
      };
      const wrapNameCell = (cell: string, type: string): string => {
        const plain = cell.replace(/<[^>]+>/g, '').trim();
        if (!type || plain.length < 3 || /^[\d.,%+\-\s]+$/.test(plain) || /^(AED|INR|OMR|SAR|QAR|USD)\b/i.test(plain)) return cell;
        const safe = plain.replace(/"/g, '&quot;');
        return `<span class="ai-drill" data-drill-type="${type}" data-drill="${safe}">${cell}</span>`;
      };

      // Build table HTML from a block of pipe-rows, or return null if it can't
      // form a table yet. Logic is identical to the original Path A / Path B —
      // factored out so both modes share it.
      const renderTable = (block: string[]): string | null => {
        const sepIdx = block.findIndex(isSepRow);
        // Path A: well-formed markdown table with separator row
        if (sepIdx > 0 && block.length > sepIdx + 1) {
          const headerRows = block.slice(0, sepIdx).map(parseRow);
          const nc = nameColOf(headerRows[0] || []);
          let html = '<div class="ai-table-wrap"><table class="ai-table"><thead>';
          headerRows.forEach(cells => {
            if (cells.length) html += '<tr>' + cells.map(c => `<th>${c}</th>`).join('') + '</tr>';
          });
          html += '</thead><tbody>';
          block.slice(sepIdx + 1).forEach(row => {
            const cells = parseRow(row);
            if (cells.length) html += '<tr>' + cells.map((c, ci) => `<td>${ci === nc.idx ? wrapNameCell(c, nc.type) : c}</td>`).join('') + '</tr>';
          });
          html += '</tbody></table></div>';
          return html;
        }
        // Path B: tolerant fallback — accept tables without separator row when
        // 2+ rows share the same column count (>= 2 cols) and no row is a separator.
        const dataRows = block.filter(r => !isSepRow(r));
        const parsed = dataRows.map(parseRow).filter(cells => cells.length >= 2);
        if (parsed.length >= 2) {
          const counts: Record<number, number> = {};
          parsed.forEach(c => { counts[c.length] = (counts[c.length] || 0) + 1; });
          const dominantCol = Number(
            Object.keys(counts).sort((a, b) => counts[Number(b)] - counts[Number(a)])[0]
          );
          const matching = parsed.filter(c => c.length === dominantCol);
          if (matching.length >= 2 && dominantCol >= 2) {
            const first = matching[0];
            const looksLikeHeader = first.every(c => c.length > 0 && c.length <= 32 && !/\d/.test(c));
            let html = '<div class="ai-table-wrap"><table class="ai-table"><thead>';
            let bodyRows: string[][];
            if (looksLikeHeader) {
              html += '<tr>' + first.map(c => `<th>${c}</th>`).join('') + '</tr>';
              bodyRows = matching.slice(1);
            } else {
              html += '<tr>' + Array(dominantCol).fill('<th></th>').join('') + '</tr>';
              bodyRows = matching;
            }
            const nc = looksLikeHeader ? nameColOf(first) : { idx: -1, type: '' };
            html += '</thead><tbody>';
            bodyRows.forEach(cells => {
              html += '<tr>' + cells.map((c, ci) => `<td>${ci === nc.idx ? wrapNameCell(c, nc.type) : c}</td>`).join('') + '</tr>';
            });
            html += '</tbody></table></div>';
            return html;
          }
        }
        return null;
      };

      while (i < lines.length) {
        if (lines[i].includes('|')) {
          const block: string[] = [];
          while (i < lines.length && lines[i].includes('|')) { block.push(lines[i]); i++; }
          const blockIsTail = i >= lines.length;
          // Only treat as a table if it's genuinely tabular (≥2 rows, or a row
          // with ≥3 cells) — a stray pipe in prose shouldn't become a table.
          const looksTabular = block.length >= 2 || block.some(l => (l.match(/\|/g) || []).length >= 2);
          // While streaming, drop the final row if it's still being typed.
          const effBlock = (streaming && blockIsTail && lastLineInProgress && block.length > 0)
            ? block.slice(0, -1) : block;
          const html = looksTabular ? renderTable(effBlock) : null;
          if (html) {
            out.push(html);
          } else if (streaming && looksTabular) {
            // Table header/separator typed but not enough rows yet — show a tiny
            // "building" indicator (inline-styled so it needs no scoped CSS)
            // instead of the raw pipes.
            out.push('<span class="ai-table-building" style="display:inline-block;margin:6px 0;font-size:12px;color:var(--text-faint,#9aa);opacity:.8;">▦ building table…</span>');
          } else {
            block.forEach(r => out.push(r));
          }
        } else { out.push(lines[i]); i++; }
      }
      result = out.join('\n');
    }

    result = result
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
    result = result.replace(/^#{1,3}\s(.+)$/gm, '<div class="ai-heading">$1</div>');
    result = result.replace(/^---+$/gm, '<hr class="ai-divider">');
    result = result.replace(/^[-•]\s(.+)$/gm, '<span class="ai-bullet">• $1</span>');
    result = result.replace(/^\d+\.\s(.+)$/gm, '<span class="ai-numbered">$1</span>');
    result = result.replace(/\n/g, '<br>');

    codeBlocks.forEach((block, idx) => {
      result = result.replace(`\x00CODE${idx}\x00`, block);
    });

    // ── Drill-down: wrap currency amounts so clicking sends a follow-up
    // "what makes up this number" query. Conservative regex — only matches
    // AED-prefixed amounts (currency) which are almost always drillable, not
    // dates, counts, or IDs. Works inside <strong>, table cells, etc. because
    // we run it AFTER markdown processing.
    if (!streamingMode) {
      result = result.replace(
        /(AED\s+[\d,]+(?:\.\d{1,2})?(?:\s*[MK])?)/g,
        '<span class="ai-drill" data-drill-type="amount" data-drill="$1" title="Tap for drill-down options">$1</span>'
      );
      // Suggested action buttons: AI emits [action:Do something] inline. We group a
      // run of them under a "Try asking" lead-in + a divider so they read as a
      // tappable menu, NOT as more of the AI's reply (owner feedback 2026-06-19).
      result = result.replace(
        /(?:[ \t]*\[action:[^\]]{1,120}\][ \t]*\n?)+/g,
        (run) => {
          const buttons = run.replace(/\[action:([^\]]{1,120})\]/g, (_m, label) => {
            const safe = label.replace(/"/g, '&quot;').trim();
            return `<button type="button" class="ai-action" data-action="${safe}">${safe}</button>`;
          }).replace(/\s+/g, ' ').trim();
          return `<div class="ai-actions"><span class="ai-actions-lead">Try asking</span>${buttons}</div>`;
        }
      );
    }
    return result;
  }

  /**
   * Drill-down handler. Wired into the component's message-bubble click
   * delegation (event delegation because innerHTML doesn't fire (click)
   * bindings on its child elements). Returns the follow-up prompt the
   * component should send, or '' if the click wasn't on a drillable span.
   */
  buildDrillPrompt(clickedValue: string, _contextText = ''): string {
    if (!clickedValue) return '';
    // Keep the VISIBLE message short and clean — the backend already sends the
    // recent conversation (incl. the answer just tapped) to the model, so we do
    // NOT paste the previous response into the user's bubble (owner feedback
    // 2026-06-19: "no need to show all this, keep context inside").
    return `Break down ${clickedValue} from your last answer — show the invoices, customers, or items behind it.`;
  }

  /**
   * Tap-to-drill MENU: given a clicked entity/number and its type (from the
   * data-drill-type attribute), return the 2-4 contextual drill options to show
   * in the popup. Tapping one sends its `prompt` as a fresh question. Shared by
   * the widget + full-page so the menus stay identical.
   */
  drillOptions(type: string, value: string, contextText = ''): { label: string; prompt: string }[] {
    const v = String(value || '').replace(/\s+/g, ' ').trim();
    if (!v) return [];
    switch (type) {
      case 'customer':
        return [
          { label: '📋 360° view',       prompt: `Give me a full 360 view of ${v}` },
          { label: '🧾 Recent invoices', prompt: `Show ${v}'s recent invoices` },
          { label: '💰 Outstanding',     prompt: `What's the outstanding balance for ${v}?` },
          { label: '📈 Sales trend',     prompt: `Show ${v}'s sales trend over the last 12 months and what's driving it` },
        ];
      case 'product':
        return [
          { label: '💵 Sales & profit', prompt: `Show sales and estimated profit for product ${v}` },
          { label: '👥 Who buys it',    prompt: `Which of my customers buy ${v}?` },
          { label: '📦 Stock & reorder', prompt: `What's the stock and reorder status for ${v}?` },
        ];
      case 'brand':
        return [
          { label: '💵 Brand sales',    prompt: `Show sales for the brand ${v}` },
          { label: '📊 Brand margin',   prompt: `What's the margin on the brand ${v}?` },
          { label: '👥 Top customers',  prompt: `Who are the top customers for the brand ${v}?` },
        ];
      case 'employee':
        return [
          { label: '💵 Their sales',    prompt: `Show ${v}'s sales this year` },
          { label: '👥 Their customers', prompt: `Which customers is ${v} responsible for?` },
        ];
      case 'amount':
      default:
        return [
          { label: '🔍 Break it down',  prompt: this.buildDrillPrompt(v, contextText) },
          { label: '📈 Monthly trend',  prompt: `Show ${v} as a month-by-month trend` },
          { label: '↔ vs last period',  prompt: `How does ${v} compare to the previous period? Show the change and what drove it.` },
        ];
    }
  }

  /** Replace the message at `index` with `msg` and push a new array ref to subscribers. */
  replaceMessage(index: number, msg: ChatMessage): void {
    const msgs = [...this.messages];
    if (!msgs[index]) return;
    msgs[index] = msg;
    this._messages$.next(msgs);
  }

  /**
   * Public, type-safe way for components to patch a single message in state.
   * Used by the chart-type switcher in the bubble so a re-render of options
   * persists in shared state (both widget + full-page stay in sync).
   */
  patchMessage(index: number, patch: Partial<ChatMessage>): void {
    const msgs = [...this.messages];
    if (!msgs[index]) return;
    msgs[index] = { ...msgs[index], ...patch };
    this._messages$.next(msgs);
  }

  /** Build ECharts options for an AI-returned chart payload. */
  buildChartOptions(chartData: ChartData): any {
    const colors = ['#16a34a', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'];
    // Shared animation polish — applied to every chart type.
    const animation = {
      animation: true,
      animationDuration: 900,
      animationEasing: 'cubicOut',
      animationDelay: (idx: number) => idx * 40
    };
    // Compact number formatter reused across axes/labels.
    const fmt = (v: number) =>
      v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` :
      v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : String(v);
    // Shared emphasis effect — accent-green glow on series hover.
    const seriesEmphasis = {
      focus: 'series',
      itemStyle: { shadowBlur: 16, shadowColor: 'rgba(22,163,74,0.45)' }
    };

    // ── PIE / DONUT ────────────────────────────────────────────────
    if (chartData.type === 'pie' || chartData.type === 'donut') {
      const isDonut = chartData.type === 'donut';
      const pieData = (chartData.series[0]?.data || []).map((v, i) => ({
        name: chartData.xAxis?.[i] || `Item ${i + 1}`, value: v
      }));
      const total = pieData.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
      return {
        ...animation,
        color: colors,
        tooltip: {
          trigger: 'item',
          formatter: (p: any) =>
            `${p.name}<br/>AED ${Number(p.value).toLocaleString('en-US', { minimumFractionDigits: 0 })} (${p.percent}%)`
        },
        legend: { type: 'scroll', bottom: 0, textStyle: { color: '#6b7280', fontSize: 11 } },
        // Donut centre label shows total; pie has no centre text.
        graphic: isDonut ? [{
          type: 'text', left: 'center', top: 'middle',
          style: {
            text: `AED ${fmt(total)}\nTotal`,
            textAlign: 'center', fill: '#374151',
            font: '600 13px sans-serif', lineDash: undefined
          }
        }] : undefined,
        series: [{
          type: 'pie',
          radius: isDonut ? ['55%', '75%'] : ['35%', '65%'],
          data: pieData,
          label: { formatter: '{b}\n{d}%', color: '#374151', fontSize: 11 },
          emphasis: { ...seriesEmphasis, itemStyle: { shadowBlur: 16, shadowColor: 'rgba(22,163,74,0.45)' } },
          itemStyle: { borderColor: '#fff', borderWidth: 2 }
        }],
        backgroundColor: 'transparent'
      };
    }

    // ── HORIZONTAL BAR ─────────────────────────────────────────────
    if (chartData.type === 'horizontalBar') {
      return {
        ...animation,
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' },
          formatter: (params: any[]) =>
            params.map(p => `<b>${p.name}</b><br/>AED ${Number(p.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`).join('<br/>')
        },
        grid: { left: '2%', right: '12%', containLabel: true, top: 10, bottom: 10 },
        xAxis: {
          type: 'value',
          axisLabel: { color: '#9ca3af', formatter: fmt },
          splitLine: { lineStyle: { color: '#eef0f2' } }
        },
        yAxis: {
          type: 'category', data: chartData.xAxis || [],
          axisLabel: { color: '#6b7280', fontSize: 11, width: 140, overflow: 'truncate' }
        },
        series: chartData.series.map((s, i) => ({
          name: s.name, type: 'bar', data: s.data,
          itemStyle: { color: colors[i % colors.length], borderRadius: [0, 4, 4, 0] },
          emphasis: seriesEmphasis,
          label: {
            show: true, position: 'right', color: '#6b7280', fontSize: 10,
            formatter: (p: any) => fmt(p.value)
          }
        })),
        backgroundColor: 'transparent'
      };
    }

    // ── STACKED BAR ────────────────────────────────────────────────
    if (chartData.type === 'stackedBar') {
      // Totals per category — shown on top of the final (top-most) series.
      const totals: number[] = [];
      const cats = chartData.xAxis || [];
      cats.forEach((_, ci) => {
        let sum = 0;
        chartData.series.forEach(s => { sum += Number(s.data[ci]) || 0; });
        totals.push(sum);
      });
      const lastIdx = chartData.series.length - 1;
      return {
        ...animation,
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' },
          formatter: (params: any[]) =>
            params.map(p => `<b>${p.seriesName}</b>: AED ${Number(p.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`).join('<br/>')
        },
        legend: { data: chartData.series.map(s => s.name), bottom: 0, textStyle: { color: '#6b7280', fontSize: 11 } },
        grid: { left: '3%', right: '4%', bottom: '14%', top: 20, containLabel: true },
        xAxis: {
          type: 'category', data: cats,
          axisLabel: { color: '#9ca3af', rotate: cats.length > 6 ? 30 : 0, fontSize: 11 },
          axisLine: { lineStyle: { color: '#e5e7eb' } }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: '#9ca3af', formatter: fmt },
          splitLine: { lineStyle: { color: '#eef0f2' } }
        },
        series: chartData.series.map((s, i) => ({
          name: s.name, type: 'bar', stack: 'total', data: s.data,
          itemStyle: { color: colors[i % colors.length] },
          emphasis: seriesEmphasis,
          barMaxWidth: 50,
          // Show the stack total only on the topmost series.
          label: i === lastIdx ? {
            show: true, position: 'top', color: '#6b7280', fontSize: 10,
            formatter: (p: any) => fmt(totals[p.dataIndex] || 0)
          } : { show: false }
        })),
        backgroundColor: 'transparent'
      };
    }

    // ── SCATTER ────────────────────────────────────────────────────
    if (chartData.type === 'scatter') {
      const symbols = ['circle', 'rect', 'triangle', 'diamond', 'pin', 'arrow'];
      const xs = chartData.xAxis || [];
      // Try to treat xAxis as numeric; fall back to category labels.
      const xsNumeric = xs.map(v => Number(v)).every(n => !isNaN(n));
      return {
        ...animation,
        tooltip: {
          trigger: 'item',
          formatter: (p: any) => `<b>${p.seriesName}</b><br/>x: ${p.value[0]}<br/>y: AED ${Number(p.value[1]).toLocaleString('en-US')}`
        },
        legend: chartData.series.length > 1 ? { data: chartData.series.map(s => s.name), bottom: 0, textStyle: { color: '#6b7280', fontSize: 11 } } : undefined,
        grid: { left: '3%', right: '4%', bottom: chartData.series.length > 1 ? '14%' : '8%', top: 20, containLabel: true },
        xAxis: {
          type: xsNumeric ? 'value' : 'category',
          data: xsNumeric ? undefined : xs,
          axisLabel: { color: '#9ca3af', fontSize: 11 },
          splitLine: { lineStyle: { color: '#eef0f2' } }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: '#9ca3af', formatter: fmt },
          splitLine: { lineStyle: { color: '#eef0f2' } }
        },
        series: chartData.series.map((s, i) => ({
          name: s.name,
          type: 'scatter',
          symbolSize: 12,
          symbol: symbols[i % symbols.length],
          data: s.data.map((v, di) => xsNumeric ? [Number(xs[di]), v] : [xs[di] ?? di, v]),
          itemStyle: { color: colors[i % colors.length] },
          emphasis: seriesEmphasis
        })),
        backgroundColor: 'transparent'
      };
    }

    // ── RADAR ──────────────────────────────────────────────────────
    if (chartData.type === 'radar') {
      const labels = chartData.xAxis || [];
      // Max of all data points across all series, * 1.1 for breathing room.
      let max = 0;
      chartData.series.forEach(s => s.data.forEach(v => { if (Number(v) > max) max = Number(v); }));
      const indicatorMax = Math.max(1, max * 1.1);
      const indicator = labels.map(name => ({ name, max: indicatorMax }));
      return {
        ...animation,
        tooltip: {
          trigger: 'item',
          formatter: (p: any) => `<b>${p.name}</b><br/>` +
            (p.value as number[]).map((v, i) => `${labels[i]}: AED ${Number(v).toLocaleString('en-US')}`).join('<br/>')
        },
        legend: chartData.series.length > 1 ? { data: chartData.series.map(s => s.name), bottom: 0, textStyle: { color: '#6b7280', fontSize: 11 } } : undefined,
        radar: {
          indicator,
          shape: 'polygon',
          splitNumber: 4,
          axisName: { color: '#6b7280', fontSize: 11 },
          splitLine: { lineStyle: { color: '#eef0f2' } },
          splitArea: { areaStyle: { color: ['rgba(248,250,252,0.4)', 'rgba(255,255,255,0)'] } },
          axisLine: { lineStyle: { color: '#e5e7eb' } }
        },
        series: [{
          type: 'radar',
          emphasis: seriesEmphasis,
          data: chartData.series.map((s, i) => ({
            name: s.name,
            value: s.data,
            itemStyle: { color: colors[i % colors.length] },
            lineStyle: { color: colors[i % colors.length], width: 2 },
            areaStyle: { color: colors[i % colors.length] + '40' }
          }))
        }],
        backgroundColor: 'transparent'
      };
    }

    // ── BAR / LINE / AREA (default) ────────────────────────────────
    const isArea = chartData.type === 'area';
    const isLineLike = chartData.type === 'line' || isArea;
    return {
      ...animation,
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => params.map(p => `<b>${p.seriesName}</b>: AED ${Number(p.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`).join('<br/>')
      },
      legend: chartData.series.length > 1 ? { data: chartData.series.map(s => s.name), textStyle: { color: '#6b7280', fontSize: 11 }, bottom: 0 } : undefined,
      grid: { left: '3%', right: '4%', bottom: chartData.series.length > 1 ? '14%' : '8%', top: 20, containLabel: true },
      xAxis: {
        type: 'category', data: chartData.xAxis || [],
        axisLabel: { color: '#9ca3af', rotate: chartData.xAxis && chartData.xAxis.length > 6 ? 30 : 0, fontSize: 11 },
        axisLine: { lineStyle: { color: '#e5e7eb' } }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#9ca3af', formatter: fmt },
        splitLine: { lineStyle: { color: '#eef0f2' } }
      },
      series: chartData.series.map((s, i) => {
        const c = colors[i % colors.length];
        // Area: stronger gradient fill (0.35 → 0). Default line: subtle (0.25 → 0).
        const areaStyle = isArea ? {
          color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
            { offset: 0, color: c + '59' /* ~0.35 */ },
            { offset: 1, color: c + '00' }
          ] }
        } : (chartData.type === 'line' ? {
          color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
            { offset: 0, color: c + '40' },
            { offset: 1, color: 'transparent' }
          ] }
        } : undefined);
        return {
          name: s.name,
          type: isLineLike ? 'line' : 'bar',
          data: s.data,
          smooth: isLineLike,
          itemStyle: { color: c },
          lineStyle: isLineLike ? { width: isArea ? 2.5 : 2, color: c } : undefined,
          areaStyle,
          showSymbol: isLineLike,
          symbolSize: 6,
          emphasis: seriesEmphasis,
          barMaxWidth: 50,
          label: !isLineLike && (chartData.xAxis?.length || 0) <= 8 ? {
            show: true, position: 'top', color: '#9ca3af', fontSize: 10,
            formatter: (p: any) => p.value >= 1e3 ? fmt(p.value) : ''
          } : undefined
        };
      }),
      backgroundColor: 'transparent'
    };
  }

  /** Time in "HH:MM AM/PM" — used as the per-message timestamp. */
  nowTime(): string {
    return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  /** Convert an ISO date → "5m", "2h", "3d", "1w". */
  relativeTime(iso: string): string {
    const t = new Date(iso).getTime();
    if (!t) return '';
    const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return s + 's';
    const m = Math.floor(s / 60);
    if (m < 60) return m + 'm';
    const h = Math.floor(m / 60);
    if (h < 24) return h + 'h';
    const d = Math.floor(h / 24);
    if (d < 7) return d + 'd';
    return Math.floor(d / 7) + 'w';
  }

  /** "12s" / "1m 5s" — duration formatter for finished-thinking summary. */
  formatDuration(ms?: number): string {
    if (!ms) return '';
    const s = Math.floor(ms / 1000);
    return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
  }
}
